import React from "react";
import { ArrowRight } from "lucide-react";
import { FIRM } from "../data/mock";

export const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 lg:py-32 bg-ivory relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16 items-start">
        {/* Left column - Image and quote */}
        <div className="lg:col-span-5 lg:sticky lg:top-32">
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-full h-full bg-gold/15 rounded-sm" />
            <div className="relative aspect-[4/5] overflow-hidden rounded-sm shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1505664194779-8beaceb93744?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                alt="Texas courthouse columns — symbolic of business and financial litigation"
                data-testid="about-image"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/30 via-transparent to-transparent" />
            </div>
          </div>

          <blockquote
            data-testid="about-quote"
            className="mt-10 pl-6 border-l-2 border-gold"
          >
            <p className="font-serif italic text-2xl text-navy leading-snug">
              "Most cases come to Dick because of his business law experience
              and the knowledge gained as a forensic accountant. The
              combination is rare — and decisive."
            </p>
          </blockquote>
        </div>

        {/* Right column - Content */}
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3 mb-6">
            <div className="gold-line" />
            <span className="text-xs tracking-[0.3em] uppercase text-navy/70 font-medium">
              About the Firm
            </span>
          </div>

          <h2
            data-testid="about-headline"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05] tracking-tight"
          >
            A trial lawyer who
            <br />
            <span className="italic text-gold">reads ledgers</span>{" "}
            like
            <br />
            most read briefs.
          </h2>

          <div className="mt-10 space-y-6 text-navy/80 text-base lg:text-lg leading-relaxed">
            <p data-testid="about-paragraph-1">
              For more than{" "}
              <strong className="text-navy">50 years</strong>, Texas forensic
              accountant, business trial lawyer and CPA{" "}
              <strong className="text-navy">Dick Bufkin</strong> has provided
              legal representation primarily in financially oriented matters.
              His practice spans business, family-law and probate litigation
              where one or more financial issues are paramount.
            </p>
            <p data-testid="about-paragraph-2">
              A substantial part of his work involves the representation of{" "}
              <strong className="text-navy">bankruptcy trustees</strong> and
              plaintiffs in cases brought to recover financial damages,
              property — or both. These often involve fraudulent transfers,
              breaches of fiduciary duty, wrongful takings, and unjust
              enrichment. Recent matters have included claims arising from
              breached construction contracts and warranty disputes between
              owners, builders, and contractors.
            </p>
            <p data-testid="about-paragraph-3">
              Most cases come to Dick, directly or indirectly, because of his
              business law experience and the specialized knowledge gained as
              a forensic accountant. That dual skill set has proven
              instrumental — both in grasping the financial issues and in
              handling them through litigation.
            </p>
            <p className="text-sm italic text-navy/60 pt-2">
              Note: The firm does not provide criminal or personal injury
              representation.
            </p>
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="/practice-areas"
              data-testid="about-cta-practice"
              className="btn-outline inline-flex items-center gap-3 px-7 py-4 text-xs tracking-[0.25em] uppercase font-medium group"
            >
              Our Practice Areas
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="/richard-bufkin"
              data-testid="about-cta-bio"
              className="inline-flex items-center gap-3 px-2 py-4 text-sm text-navy font-medium hover:text-gold transition-colors group"
            >
              <span className="link-underline">Read Dick's Full Bio</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
